import type { CSSProperties, ReactNode, ComponentPropsWithoutRef } from "react";
import type { IconName } from "@blueprintjs/icons";
import { UiSchema } from "@rjsf/utils";
import type { ComponentImportRegistry } from "./componentImports";
import { type JSONSchema } from "json-schema-to-ts";
import { Permission } from "domain/application/permissions/Permissions";
import { isPermitted } from "./jm";
import type { WindowSnapRegion } from "lib/window/windowSnapping";
import type { PermissionCheckInput } from "src/gql/graphql";

export type ComponentName = keyof ComponentImportRegistry;

// We won't require all uiprops to be set either at init or in the state.
// The clients of the props can provide defaults if they don't exist.
export type UIProps = Partial<{
  label: ReactNode;

  // Optional label specifically for the taskbutton
  // otherwise "label" will be used
  taskButtonLabel?: ReactNode | undefined;

  icon: IconName;

  resizable: boolean;

  minimal: boolean;

  pinnable: boolean;
  pinned: boolean;

  maximised: boolean;
  minimised: boolean;
  minimisable: boolean;
  maximisable: boolean;

  maxWidth: number | string;
  maxHeight: number | string;
  minWidth: number | string;
  minHeight: number | string;
  width: number;
  height: number;
  restoredWidth: number;
  restoredHeight: number;

  xPosition: number;
  yPosition: number;
  xRestoredPosition: number;
  yRestoredPosition: number;
  snapRegion: WindowSnapRegion;
  snapSplitX: number;
  snapSplitY: number;
  centered: boolean;

  isActive: boolean;
  zIndex: CSSProperties["zIndex"];
  tools: ReactNode;

  className: string;
}>;

export type ComponentProps<T extends ComponentName> = ComponentPropsWithoutRef<
  ComponentImportRegistry[T]
>;

export type ComponentState<T extends ComponentName> = {
  componentId: string;
  componentType: T;
  componentProps?: ComponentProps<T>;
  uiProps: UIProps;
};

export type ComponentRouteState<T extends ComponentName> = {
  componentType: T;
  componentProps?: ComponentProps<T>;
  uiProps?: UIProps;
};

type ComponentRouteOptions<T extends ComponentName> = {
  getPathParts?: (props: {
    componentProps?: ComponentProps<T>;
  }) => Array<string | number | boolean | null | undefined>;
  resolve?: (props: {
    pathParts: string[];
    searchParams: URLSearchParams;
  }) => ComponentRouteState<T> | null;
};

export const isValidComponentType = (
  key: string,
): key is keyof ComponentImportRegistry => {
  return Object.prototype.hasOwnProperty.call(componentOptions, key);
};

export type ComponentOptionsRegistryEntry = {
  defaultUiOptions: Partial<UIProps>;
  component?: ComponentName;
  defaultComponentProps?: ComponentProps<ComponentName>;
  config?: {
    schema?: JSONSchema;
    uiSchema?: UiSchema;
  };
  requiredPermissions?: PermissionCheckInput[];
  showInLauncher?: boolean;
  routing?: ComponentRouteOptions<ComponentName>;
  canOpen?: () => Promise<boolean> | boolean;
};

// IMPORTANT: Do not import this anywhere except into ProxyComponent,
// as it introduces circular deps and breaks HMR
export const componentOptions: Record<
  ComponentName,
  ComponentOptionsRegistryEntry
> = {
  JobWizard: {
    defaultUiOptions: {
      label: "New Job",
      maximised: false,
      width: 800,
      resizable: false,
      maximisable: false,
      minimisable: false,
    },
    showInLauncher: false,
  },
  JobPhotoUpload: {
    defaultUiOptions: {
      label: "Job Photo Upload",
      minimisable: false,
      maximisable: false,
      maximised: false,
      width: 500,
    },
    showInLauncher: true,
    canOpen: () =>
      isPermitted([{ permission: Permission.Query_Upload_Request_Url }]),
  },
  ProductImport: {
    defaultUiOptions: {
      label: "Product Import",
      maximised: true,
    },
    showInLauncher: false,
  },
  SchemaVersionEditor: {
    defaultUiOptions: {
      maximised: true,
      minHeight: "500px",
      minWidth: "1200px",
    },
    showInLauncher: false,
  },
  PurchaseOrderEditor: {
    defaultUiOptions: {
      maximised: true,
      className: "!p-0",
      minHeight: "500px",
      minWidth: "800px",
    },
    showInLauncher: false,
  },
  ReceiveStockEditor: {
    defaultUiOptions: {
      label: "Receive Stock",
      maximised: true,
      className: "!p-0",
      minHeight: "600px",
      minWidth: "1000px",
    },
    showInLauncher: false,
  },
  StockWithdrawalEditor: {
    defaultUiOptions: {
      label: "Withdraw Stock",
      maximised: true,
      className: "!p-0",
      minHeight: "600px",
      minWidth: "1000px",
    },
    showInLauncher: false,
  },
  StockTakeEditor: {
    defaultUiOptions: {
      label: "Stock Take",
      maximised: true,
      className: "!p-0",
      minHeight: "600px",
      minWidth: "1000px",
    },
    showInLauncher: false,
  },
  StockTransferEditor: {
    defaultUiOptions: {
      label: "Stock Transfer",
      maximised: true,
      className: "!p-0",
      minHeight: "600px",
      minWidth: "1200px",
    },
    showInLauncher: false,
  },
  AutoScreen: {
    defaultUiOptions: {
      //label: <Skeleton className="max-w-32" />, // This breaks HMR
      maximised: true,
      minHeight: 350,
      minWidth: 460,
    },
    showInLauncher: true,
    canOpen: () => false,
    routing: {
      getPathParts: ({ componentProps }) => {
        const { screenId, initialTab, initialPath } =
          componentProps as ComponentProps<"AutoScreen">;
        return [screenId, initialTab, ...(initialPath ?? [])];
      },
      resolve: ({ pathParts }) => {
        const [screenId, initialTab, ...initialPath] = pathParts;

        if (!screenId) {
          return null;
        }

        return {
          componentType: "AutoScreen",
          componentProps: {
            screenId,
            ...(initialTab ? { initialTab } : {}),
            ...(initialPath.length > 0 ? { initialPath } : {}),
          },
        };
      },
    },
  },
  RecordScreen: {
    defaultUiOptions: {
      //label: <Skeleton className="max-w-32" />, // This breaks HMR
      maximised: false,
      width: 1024,
      height: 768,
      minHeight: 350,
      minWidth: 710,
    },
    showInLauncher: false,
    canOpen: () => false,
    routing: {
      getPathParts: ({ componentProps }) => {
        const { recordType, recordId, initialTab, initialPath } =
          componentProps as ComponentProps<"RecordScreen">;
        return [recordType, recordId, initialTab, ...(initialPath ?? [])];
      },
      resolve: ({ pathParts, searchParams }) => {
        const [recordType, recordId, initialTab, ...initialPath] = pathParts;

        if (!recordType || !recordId) {
          return null;
        }

        return {
          componentType: "RecordScreen",
          componentProps: {
            recordType,
            recordId,
            ...(initialTab ? { initialTab } : {}),
            ...(initialPath.length > 0 ? { initialPath } : {}),
            context: searchParams.get("context") ?? "window",
          },
        };
      },
    },
  },
  AutoChart: {
    defaultUiOptions: {
      //label: <Skeleton />,
      maximised: true,
    },
    showInLauncher: false,
    canOpen: () => false,
  },
  // SupplierEditor: {
  //   defaultUiOptions: {
  //     label: "Supplier",
  //     width: 500,
  //     height: 500,
  //     resizable: true,
  //     maximisable: false,
  //     minimisable: false,
  //   },
  //   showInLauncher: false,
  // },
  // SupplierWizard: {
  //   defaultUiOptions: {
  //     label: "New Supplier",
  //     width: 800,
  //     height: 650,
  //     resizable: false,
  //     maximisable: false,
  //     minimisable: false,
  //   },
  //   showInLauncher: false,
  // },
  QuoteRequestEditor: {
    defaultUiOptions: {
      label: "Supplier Quote Request",
      maximised: true,
    },
    showInLauncher: true,
  },
  SalesOrderEditor: {
    defaultUiOptions: {
      label: "Sales Order",
      maximised: true,
    },
    showInLauncher: true,
  },
  QuoteEditor: {
    defaultUiOptions: {
      label: "Quote",
      maximised: true,
    },
    showInLauncher: true,
  },
  SupplierQuoteEditor: {
    defaultUiOptions: {
      label: "Supplier Quote",
      maximised: true,
    },
    showInLauncher: true,
  },
  CostingEditor: {
    defaultUiOptions: {
      label: "Costing",
      maximised: true,
    },
    showInLauncher: true,
  },
  JobEmailEditor: {
    defaultUiOptions: {
      label: "Email",
      maximised: true,
    },
    showInLauncher: false,
  },
  DocumentTemplateEditor: {
    defaultUiOptions: {
      label: "Document Editor",
      maximised: true,
    },
    showInLauncher: false,
  },
  ApiBrowser: {
    defaultUiOptions: {
      label: "API Browser",
      maximised: true,
    },
    showInLauncher: true,
  },
  StartMenu: {
    defaultUiOptions: {
      maximised: true,
      minimal: true,
    },
  },
  PanelConfigEditor: {
    defaultUiOptions: {
      label: "Panel Configuration",
      maximised: true,
      minimised: false,
      minHeight: 500,
      minWidth: 900,
      className: "!p-0",
    },
  },
  ChartPanelConfigDialog: {
    defaultUiOptions: {
      label: "Chart Panel Configuration",
      maximised: true,
      minimised: false,
    },
  },
  ListPanelConfigDialog: {
    defaultUiOptions: {
      label: "List Panel Configuration",
      maximised: true,
      minimised: false,
      minHeight: 500,
      minWidth: 900,
      className: "!p-0",
    },
  },
  QuoteTemplateEditor: {
    defaultUiOptions: {
      label: "Quote Template",
      maximised: true,
    },
    showInLauncher: true,
  },
  CostingTemplateEditor: {
    defaultUiOptions: {
      label: "Costing Template",
      maximised: true,
    },
    showInLauncher: true,
  },
};
